import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pill } from '@/components/widgets/Pill';
import { adminSymbols } from '@/api/admin';

/**
 * 合约规格卡 — XAUUSD 主品种参数 + 当前生效预设。
 *
 * 数据源说明(§9.4):
 * - 跟单参数(最小手/步长/比例/上限/冷却/预设)+ 合约规格(digits/contractSize/
 *   commission/swapLong/swapShort/session/base/quote)均走 admin 真实端点
 *   GET /admin/api/v1/symbols + /param-presets。规格列来自 tradable_symbols(迁移 0013),
 *   有值显示、缺省「—」,【不编造假数字】。
 * - 「实时延迟 ms」需后端暴露连接 RTT 字段(当前无),故不显示(不编造延迟数字)。
 */
export function SymbolSpec() {
  const symQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list });
  const presetsQ = useQuery({ queryKey: ['admin', 'param-presets'], queryFn: adminSymbols.paramPresets });

  const sym = symQ.data?.[0];
  const preset = presetsQ.data?.find((p) => p.code === sym?.currentPresetCode);
  const num = (v: number | undefined, d = 2) => (v == null ? '—' : v.toFixed(d));

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>合约规格 · {sym?.symbolName ?? 'XAUUSD'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        <Row label="代码" value={sym?.symbolName ?? 'XAUUSD'} />
        <Row label="基础 / 报价货币" value={`${sym?.baseAsset ?? '—'} / ${sym?.quoteAsset ?? '—'}`} />
        <Row label="小数位数" value={<span className="num">{sym?.digits ?? '—'}</span>} />
        <Row label="1 标准手" value={<span className="num">{sym?.contractSize ?? '—'}</span>} />
        <Row label="主账户最小手" value={<span className="num">{num(sym?.mainMinLot)}</span>} />
        <Row label="主账户步长" value={<span className="num">{num(sym?.mainLotStep)}</span>} />
        <Row label="雇员模拟步长" value={<span className="num">{num(preset?.virtualLotStep)}</span>} />
        <Row label="佣金" value={<span className="num">{sym?.commission == null ? '—' : `$${num(sym.commission)}`}</span>} />
        <Row label="隔夜利息 多/空" value={<span className="num">{num(sym?.swapLong)} / {num(sym?.swapShort)}</span>} />
        <Row label="交易时段 (UTC)" value={<span className="num">{sym?.sessionUtcOpen && sym?.sessionUtcClose ? `${sym.sessionUtcOpen}–${sym.sessionUtcClose}` : '—'}</span>} />
        <div className="pt-2 mt-2 border-t border-line space-y-2">
          <div className="text-xs text-ink-2">当前生效预设</div>
          <div className="flex items-center gap-2">
            <Pill tone="brand">{sym?.currentPresetCode ?? '—'}</Pill>
            {sym?.pending && <Pill tone="warn">有待生效</Pill>}
          </div>
          <div className="text-xs text-ink-3">{preset?.description ?? ''}</div>
          <Row label="跟单比例" value={<span className="num">{sym?.followRatio?.toFixed(2) ?? '—'}x</span>} />
          <Row label="毛持仓预警线" value={<span className="num">{sym?.maxTotalPosition?.toFixed(2) ?? '—'} lot</span>} />
          <Row label="冷却" value={<span className="num">{sym?.cooldownSeconds ?? '—'} s</span>} />
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-ink-2">{label}</span>
      <span className="text-sm text-ink-1">{value}</span>
    </div>
  );
}
